import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Apple, DownloadCloud, Monitor, MonitorSmartphone } from 'lucide-react';
import LandingShell from '@/components/landing/LandingShell';
import SEO from '@/components/common/SEO';
import { motion } from 'framer-motion';
import { getDownloadsSeo } from '@/lib/seo/publicMetadata';

const LAUNCHER_DOWNLOAD_URL = 'https://mapleos.ai/downloads/launcher/MapleOSLauncher-1.0.4.0.exe';

const Badge = ({ text }: { text: string }) => (
  <span className="mb-2 rounded-full bg-yellow-400 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-black">
    {text}
  </span>
);

const Downloads = () => {
  const [showLauncherInstructions, setShowLauncherInstructions] = useState(false);
  const seo = getDownloadsSeo();

  return (
    <LandingShell showSectionLinks={false}>
      <SEO {...seo} />
      <div className="mx-auto w-full max-w-7xl px-6 py-10 md:py-14">

        <div className="space-y-8 rounded-2xl bg-card p-6 shadow-card md:p-10">
          <div className="space-y-3">
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Downloads</h1>
            <p className="max-w-3xl text-sm text-muted-foreground">
              Download the MapleOS launcher and desktop installers here. Your Maple Market library now lives inside Maple Market.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              onMouseEnter={() => setShowLauncherInstructions(true)}
              onMouseLeave={() => setShowLauncherInstructions(false)}
            >
              <Card className="border border-border shadow-lg transition-transform hover:scale-[1.02]">
                <CardContent className="flex flex-col items-center p-6 text-center">
                  <Badge text="Alpha" />
                  <MonitorSmartphone size={48} className="mb-4 text-primary" />
                  <h2 className="mb-2 text-xl font-semibold">Launcher</h2>
                  <p className="mb-4 text-muted-foreground">
                    Download the MapleOS launcher for desktop installs.
                  </p>
                  <Button asChild variant="default" size="lg">
                    <a href={LAUNCHER_DOWNLOAD_URL} download>
                      <DownloadCloud className="mr-2 h-4 w-4" />
                      Download Launcher
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <Card className="border border-border opacity-50 shadow-md">
                <CardContent className="flex flex-col items-center p-6 text-center">
                  <Badge text="Coming Soon" />
                  <Monitor size={48} className="mb-4 text-muted-foreground" />
                  <h2 className="mb-2 text-xl font-semibold">Linux</h2>
                  <p className="mb-4 text-muted-foreground">Linux downloads are coming soon.</p>
                  <Button variant="outline" disabled>
                    Coming Soon
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <Card className="border border-border opacity-50 shadow-md">
                <CardContent className="flex flex-col items-center p-6 text-center">
                  <Badge text="Coming Soon" />
                  <Apple size={48} className="mb-4 text-muted-foreground" />
                  <h2 className="mb-2 text-xl font-semibold">OSX</h2>
                  <p className="mb-4 text-muted-foreground">OSX downloads are coming soon.</p>
                  <Button variant="outline" disabled>
                    Coming Soon
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {showLauncherInstructions && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="mx-auto max-w-2xl"
            >
              <div className="rounded-lg border border-border bg-background p-6 text-left text-sm text-muted-foreground shadow-lg">
                <p className="font-semibold">Installation Instructions:</p>
                <ol className="list-inside list-decimal space-y-1">
                  <li>Click "Download Launcher" to get the installer.</li>
                  <li>If Windows SmartScreen appears, click "More info" then "Run anyway".</li>
                  <li>You may see "Unknown Publisher" — this is expected while the launcher is in alpha.</li>
                  <li>Follow the on-screen prompts to complete the installation.</li>
                </ol>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </LandingShell>
  );
};

export default Downloads;